/**
 * @fileoverview Connect controlled events to month, week, day, and agenda presentations. The parent owns event loading/actions; this layer owns view/date navigation and passes current-user context to shared event controls. `utils/calendarUtils.js` owns layout/time semantics. Remembered browser navigation is separate from persisted event state or permission to mutate an event.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import moment from 'moment-timezone';
import api from '../../api';
import './bespoke-calendar.css';

// Import our bespoke calendar components
import CalendarNavigation from './components/CalendarNavigation';
import MonthGridView from './views/MonthGridView';
import WeekView from './views/WeekView';
import DayView from './views/DayView';
import AgendaView from './views/AgendaView';

// Import utilities
import { CALENDAR_VIEWS, navigation } from './utils/calendarUtils';



const BespokeCalendar = ({ 
  events = [],
  scheduleXCalendarsConfig = {},
  onEventClick,
  onDeleteEvent,
  onConfirmResponsibility,
  onAddEvent,
  onViewChange,
  assignees = []
}) => {
  // Calendar state — restore from localStorage so refresh returns to the same view/date
  const [currentDate, setCurrentDate] = useState(

  () => {
    try {
      const saved = localStorage.getItem('calendarCurrentDate');
      if (saved) {
        const restored = moment(saved);
        if (restored.isValid()) return restored;
      }
    } catch {}
    return moment();
  });
  const [currentView, setCurrentView] = useState(() => {
    try {
      const saved = localStorage.getItem('calendarCurrentView');
      if (saved && Object.values(CALENDAR_VIEWS).includes(saved)) return saved;
    } catch {}
    return CALENDAR_VIEWS.MONTH_GRID;
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  


  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    let isMounted = true;
    api.get('/user')
      .then(res => { if (isMounted) setCurrentUser(res.data); })
      .catch(() => {});
    return () => { isMounted = false; };
  }, []);



  useEffect(() => {
    if (onViewChange) {
      onViewChange(currentView, currentDate);
    }
  }, [currentView, currentDate, onViewChange]);

  // Store calendar colors globally for event styling
  useMemo(() => {
    window.calendarColors = scheduleXCalendarsConfig;
  }, [scheduleXCalendarsConfig]);

  // Handle date navigation
  const handleDateChange = useCallback(

  (newDate) => {
    const d = moment(newDate);
    setCurrentDate(d);
    try { localStorage.setItem('calendarCurrentDate', d.toISOString()); } catch {}
  }, []);

  // Handle view change
  const handleViewChange = useCallback(

  (newView) => {
    setCurrentView(newView);
    try { localStorage.setItem('calendarCurrentView', newView); } catch {}
  }, []);

  // Handle event click
  const handleEventClickCallback = useCallback(

  (event, e) => {
    if (onEventClick) {
      onEventClick(event);
    }
  }, [onEventClick]);

  // Handle event edit
  const handleEventEdit = useCallback(

  (event) => {
    if (onEventClick) {
        onEventClick(event);
    }
  }, [onEventClick]);

  // Handle event delete
  const handleEventDelete = useCallback(

  (eventId) => {
    if (onDeleteEvent) {
      onDeleteEvent(eventId);
    }
  }, [onDeleteEvent]);

  // Handle responsibility confirmation
  const handleConfirmResponsibility = useCallback(

  (eventId) => {
    if (onConfirmResponsibility) {
      onConfirmResponsibility(eventId);
    }
  }, [onConfirmResponsibility]);

  // Handle date click (for creating new events)
  const handleDateClick = useCallback(

  (date) => {
    if (onAddEvent) {
      // Create a new event at the clicked date/time
      const newEventData = {
        start: date.format('YYYY-MM-DD HH:mm'),
        end: date.clone().add(1, 'hour').format('YYYY-MM-DD HH:mm'),
        title: '',
        description: '',
        allDay: false
      };
      // For now, just trigger the add event handler
      // In a full implementation, this might pre-populate the form
      onAddEvent();
    }
  }, [onAddEvent]);

  // Handle time slot click (for creating events at specific times)
  const handleTimeSlotClick = useCallback(

  (dateTime) => {
    if (onAddEvent) {
      console.log('Time slot clicked for new event:', dateTime.format('YYYY-MM-DD HH:mm'));
      onAddEvent();
    }
  }, [onAddEvent]);

  // Handle event drag and drop
  const handleEventDrop = useCallback(

  (event, newDateTime) => {
    console.log('Event dropped:', event.title, 'to', newDateTime.toDate().toISOString());
    
    // Calculate the duration to maintain event length
    const originalStart = moment(event.start);
    const originalEnd = moment(event.end);
    const duration = originalEnd.diff(originalStart);
    
    const updatedEvent = {
      ...event,
      // Preserve ISO 8601 (UTC) to avoid timezone drift on edit
      start: newDateTime.toDate().toISOString(),
      end: newDateTime.clone().add(duration).toDate().toISOString()
    };
    
    // Trigger edit with updated times
    if (onEventClick) {
      onEventClick(updatedEvent);
    }
  }, [onEventClick]);

  // Handle add event
  const handleAddEvent = useCallback(

  () => {
    if (onAddEvent) {
      onAddEvent();
    }
  }, [onAddEvent]);

  // Render the appropriate view
  const renderCalendarView =

  () => {
    const commonProps = {
      currentDate,
      events,
      onEventClick: handleEventClickCallback,
      onEventEdit: handleEventEdit,
      onEventDelete: handleEventDelete,
      onConfirmResponsibility: handleConfirmResponsibility,
      onEventDrop: handleEventDrop,
      assignees,
      currentUser
    };

    switch (currentView) {
      case CALENDAR_VIEWS.MONTH_GRID:
        return (
          <MonthGridView
            {...commonProps}
            onDateClick={handleDateClick}
          />
        );

      case CALENDAR_VIEWS.WEEK:
        return (
          <WeekView
            {...commonProps}
            onTimeSlotClick={handleTimeSlotClick}
          />
        );

      case CALENDAR_VIEWS.DAY:
        return (
          <DayView
            {...commonProps}
            onTimeSlotClick={handleTimeSlotClick}
          />
        );

      case CALENDAR_VIEWS.MONTH_AGENDA:
        return (
          <AgendaView
            {...commonProps}
          />
        );

      default:
        return (
          <MonthGridView
            {...commonProps}
            onDateClick={handleDateClick}
          />
        );
    }
  };

  return (
    <div className="bespoke-calendar">
      <CalendarNavigation
        currentDate={currentDate}
        currentView={currentView}
        onDateChange={handleDateChange}
        onViewChange={handleViewChange}
        onAddEvent={handleAddEvent}
      />
      
      <div className="calendar-view-container">
        {renderCalendarView()}
      </div>
      

    </div>
  );
};

export default BespokeCalendar; 